/**
 * Jul-2026 (User-Wunsch) — zusätzliche Empfänger für den Lead-Wochenreport
 * (z. B. Verkaufsleiter) verwalten. Report geht immer an die Hauptsitz-E-Mail
 * plus die hier gepflegten Adressen.
 * Aug-2026 (User-Wunsch) — ins Zahnrad (Lead-Regeln) umgezogen: Inhalt als
 * wiederverwendbare Inline-Sektion inkl. „Jetzt senden".
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, X, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const ReportRecipientsSection = () => {
    const [recipients, setRecipients] = useState([]);
    const [mainEmail, setMainEmail] = useState("");
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get("/dealer/leads/report-recipients")
            .then((r) => { setRecipients(r.data.recipients || []); setMainEmail(r.data.main_email || ""); })
            .catch(() => toast.error("Empfänger konnten nicht geladen werden"))
            .finally(() => setLoading(false));
    }, []);

    const add = () => {
        const e = input.trim().toLowerCase();
        if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Bitte gültige E-Mail eingeben"); return; }
        if (recipients.includes(e) || e === mainEmail.toLowerCase()) { toast.error("Empfänger ist bereits enthalten"); return; }
        if (recipients.length >= 10) { toast.error("Maximal 10 zusätzliche Empfänger"); return; }
        setRecipients((r) => [...r, e]);
        setInput("");
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/dealer/leads/report-recipients", { recipients });
            toast.success("Report-Empfänger gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const sendNow = async () => {
        setSending(true);
        try {
            const { data: r } = await api.post("/dealer/leads/weekly-report/send");
            toast.success(`Wochenreport an ${r.to_email}${r.extra_recipients ? ` + ${r.extra_recipients} weitere` : ""} gesendet (${r.leads} Leads, ${r.sites} Standorte)${r.mocked ? " — Demo-Modus, E-Mail wird geloggt" : ""}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Report-Versand fehlgeschlagen"); }
        setSending(false);
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center" data-testid="report-recipients-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Lade…
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="report-recipients-section">
            <p className="text-xs text-gray-500">
                Der Wochenreport (alle Leads + Antwortzeiten je Standort) läuft automatisch jeden Montag und geht immer an{" "}
                <span className="font-bold">{mainEmail}</span>. Hier können zusätzliche Empfänger (z. B. Verkaufsleiter) ergänzt werden.
            </p>
            <ul className="space-y-1.5" data-testid="report-recipients-list">
                {recipients.length === 0 && (
                    <li className="text-xs text-gray-400" data-testid="report-recipients-empty">Keine zusätzlichen Empfänger.</li>
                )}
                {recipients.map((r) => (
                    <li key={r} className="flex items-center gap-2 border border-gray-200 rounded-sm px-2.5 py-1.5 text-sm" data-testid={`report-recipient-${r}`}>
                        <Mail className="w-3.5 h-3.5 text-gray-400" /> {r}
                        <button type="button" onClick={() => setRecipients((x) => x.filter((v) => v !== r))}
                            className="ml-auto p-1 text-gray-400 hover:text-red-600"
                            data-testid={`report-recipient-remove-${r}`}>
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </li>
                ))}
            </ul>
            <div className="flex items-center gap-2">
                <input
                    type="email" value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder="verkaufsleiter@firma.de"
                    className="flex-1 h-9 px-2 rounded-sm border border-gray-300 text-sm"
                    data-testid="report-recipient-input"
                />
                <button type="button" onClick={add}
                    className="inline-flex items-center gap-1 text-xs font-bold border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 hover:bg-gray-50"
                    data-testid="report-recipient-add-btn">
                    <Plus className="w-3.5 h-3.5" /> Hinzufügen
                </button>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button
                    type="button" onClick={sendNow} disabled={sending}
                    className="inline-flex items-center gap-1.5 text-xs font-bold border-2 border-[#111827] text-[#111827] rounded-sm px-3 py-2 hover:bg-gray-50 disabled:opacity-60"
                    title="Wochenreport jetzt sofort an alle Empfänger senden"
                    data-testid="report-send-now-btn"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Jetzt senden
                </button>
                <button
                    type="button" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#111827] bg-[#FFCC00] hover:brightness-95 rounded-sm px-4 py-2 disabled:opacity-60"
                    data-testid="report-recipients-save"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
                </button>
            </div>
        </div>
    );
};
